import { useEffect, useRef, type MutableRefObject } from 'react';
import type { WsEvent } from '@fixflow/shared-types';
import { broadcastDisplayIdle, broadcastToDisplay } from '@/lib/api/display-staff.js';

/** Return display to idle when staff leaves the wizard or stops editing a draft. */
const DRAFT_INACTIVITY_IDLE_MS = 45_000;

type UseDisplayTicketDraftMirrorOptions = {
  storeId: string | undefined;
  /** When false, no draft broadcasts and inactivity timer is cleared. */
  enabled: boolean;
  /** Built draft event, or null when there is nothing to show. */
  event: WsEvent | null;
  /** Set true before navigate after ticket create so API ticket review is not cleared. */
  skipIdleOnUnmountRef?: MutableRefObject<boolean>;
};

/**
 * Mirrors an in-progress ticket draft to the customer display.
 * - Sends idle when the page unmounts (staff left the wizard).
 * - Sends idle after {@link DRAFT_INACTIVITY_IDLE_MS} without draft changes.
 * - Sends idle when draft content is cleared after a preview was shown.
 */
export function useDisplayTicketDraftMirror({
  storeId,
  enabled,
  event,
  skipIdleOnUnmountRef,
}: UseDisplayTicketDraftMirrorOptions) {
  const publishedRef = useRef(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current !== undefined) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = undefined;
    }
  };

  const sendIdle = (sid: string) => {
    publishedRef.current = false;
    clearInactivityTimer();
    void broadcastDisplayIdle(sid);
  };

  const scheduleInactivityIdle = (sid: string) => {
    clearInactivityTimer();
    if (!publishedRef.current) return;
    inactivityTimerRef.current = setTimeout(() => sendIdle(sid), DRAFT_INACTIVITY_IDLE_MS);
  };

  useEffect(() => {
    if (!enabled || !storeId) {
      clearInactivityTimer();
      return;
    }

    if (!event) {
      if (publishedRef.current) sendIdle(storeId);
      return;
    }

    let cancelled = false;
    void broadcastToDisplay(storeId, event).then(() => {
      if (cancelled) return;
      publishedRef.current = true;
      scheduleInactivityIdle(storeId);
    });

    return () => {
      cancelled = true;
    };
  }, [storeId, enabled, event]);

  useEffect(() => {
    return () => {
      clearInactivityTimer();
      if (!storeId || skipIdleOnUnmountRef?.current || !publishedRef.current) return;
      void broadcastDisplayIdle(storeId);
    };
  }, [storeId, skipIdleOnUnmountRef]);
}
